/**
 * /posvenda/assistencias/:id — Detalhe + timeline + actions de transicao.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiArrowUpCircle,
  FiCheckCircle,
  FiMessageCircle,
  FiPlay,
  FiRefreshCw,
  FiRotateCcw,
  FiXCircle,
} from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import EntityHeader from "../../../components/EntityHeader";
import KpiStrip from "../../../components/KpiStrip";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import {
  cancelarAssistencia,
  comentarAssistencia,
  escalonarAssistencia,
  formatarHorasRestantes,
  iniciarAnalise,
  iniciarResolucao,
  obterAssistencia,
  PRIORIDADE_LABELS,
  PRIORIDADE_TONES,
  reabrirAssistencia,
  resolverAssistencia,
  SITUACAO_LABELS,
  SITUACAO_TONES,
  TIPO_LABELS,
} from "../../../services/posvenda";
import { PageContainer } from "../../../styles/shared";

import { fmtData } from "../../Orcamentos/helpers";
import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Pill,
  SectionTitle,
  Select,
  Textarea,
} from "../../Orcamentos/styles";


// Cor por tipo de evento na timeline
const EVENTO_TONES = {
  ABERTURA: "info",
  MUDANCA_SITUACAO: "info",
  ESCALONAMENTO: "warning",
  COMENTARIO: "neutral",
  RESOLUCAO: "success",
  CANCELAMENTO: "neutral",
  REABERTURA: "warning",
};


export default function AssistenciaDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { request, hasAllPermissions, isMaster } = useAuth();

  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmando, setConfirmando] = useState(null);
  const [texto, setTexto] = useState("");
  const [novoResponsavelId, setNovoResponsavelId] = useState("");
  const [usuarios, setUsuarios] = useState([]);

  const podeResolver = isMaster || hasAllPermissions(["assistencia:resolve"]);
  const podeEscalonar = isMaster || hasAllPermissions(["assistencia:escalonar"]);

  const carregar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await obterAssistencia(request, id);
      setA(data);
    } catch (err) {
      setError(err?.message || "Erro ao carregar.");
    } finally { setLoading(false); }
  }, [request, id]);

  useEffect(() => { carregar(); }, [carregar]);

  // Carrega usuarios (para escalonar). silentError: cargo com
  // assistencia:escalonar mas sem usuario:view cai no fallback vazio
  // sem toast de permissao na carga da pagina.
  useEffect(() => {
    if (!podeEscalonar) return;
    // /api/usuarios e paginado custom: {data, total, pagina, por_pagina}.
    // Lemos `data` (nao `results`); cobrimos array puro/`results` por seguranca.
    request("/api/usuarios?por_pagina=100", { silentError: true })
      .then((p) => setUsuarios(Array.isArray(p) ? p : p?.data || p?.results || []))
      .catch(() => {});
  }, [request, podeEscalonar]);

  const executar = useCallback(async () => {
    if (!a || !confirmando) return;
    let promise;
    if (confirmando === "iniciar_analise") promise = iniciarAnalise(request, id);
    else if (confirmando === "iniciar_resolucao") promise = iniciarResolucao(request, id);
    else if (confirmando === "resolver") {
      if (!texto.trim()) throw new Error("Resolução é obrigatória.");
      promise = resolverAssistencia(request, id, { resolucao: texto });
    } else if (confirmando === "cancelar") {
      if (!texto.trim()) throw new Error("Motivo obrigatório.");
      promise = cancelarAssistencia(request, id, { motivo: texto });
    } else if (confirmando === "reabrir") {
      if (!texto.trim()) throw new Error("Motivo obrigatório.");
      promise = reabrirAssistencia(request, id, { motivo: texto });
    } else if (confirmando === "escalonar") {
      if (!novoResponsavelId) throw new Error("Selecione novo responsável.");
      if (!texto.trim()) throw new Error("Motivo obrigatório.");
      promise = escalonarAssistencia(request, id, {
        novo_responsavel_id: novoResponsavelId, motivo: texto,
      });
    } else if (confirmando === "comentar") {
      if (!texto.trim()) throw new Error("Comentário não pode estar vazio.");
      promise = comentarAssistencia(request, id, { texto });
    }
    if (promise) {
      const data = await promise;
      setA(data);
      setConfirmando(null);
      setTexto("");
      setNovoResponsavelId("");
    }
  }, [confirmando, texto, novoResponsavelId, request, id, a]);

  if (loading && !a) return <PageContainer><EmptyMsg>Carregando...</EmptyMsg></PageContainer>;
  if (!a) {
    return (
      <PageContainer>
        <EntityHeader title="Erro" actions={[{
          label: "Voltar", icon: <FiArrowLeft size={14} />,
          onClick: () => navigate("/posvenda/assistencias"),
        }]} />
        {error && <ErrorMsg>{error}</ErrorMsg>}
      </PageContainer>
    );
  }

  const actions = [
    { label: "Voltar", icon: <FiArrowLeft size={14} />, onClick: () => navigate("/posvenda/assistencias") },
    { label: "Atualizar", icon: <FiRefreshCw size={14} />, onClick: carregar },
  ];

  // Iniciar análise/resolução e Reabrir mexem no ciclo de atendimento:
  // são de quem resolve (`assistencia:resolve`), não de quem abre chamado.
  if (a.situacao === "ABERTA" && podeResolver) {
    actions.push({
      label: "Iniciar análise", icon: <FiPlay size={14} />,
      onClick: () => setConfirmando("iniciar_analise"), primary: true,
    });
  }
  if (a.situacao === "EM_ANALISE" && podeResolver) {
    actions.push({
      label: "Iniciar resolução", icon: <FiPlay size={14} />,
      onClick: () => setConfirmando("iniciar_resolucao"), primary: true,
    });
  }
  if (["EM_ANALISE", "EM_RESOLUCAO"].includes(a.situacao) && podeResolver) {
    actions.push({
      label: "Resolver", icon: <FiCheckCircle size={14} />,
      onClick: () => setConfirmando("resolver"),
      primary: a.situacao === "EM_RESOLUCAO",
    });
  }
  if (!["RESOLVIDA", "CANCELADA"].includes(a.situacao) && podeResolver) {
    actions.push({
      label: "Cancelar", icon: <FiXCircle size={14} />,
      onClick: () => setConfirmando("cancelar"),
    });
  }
  if (["RESOLVIDA", "CANCELADA"].includes(a.situacao) && podeResolver) {
    actions.push({
      label: "Reabrir", icon: <FiRotateCcw size={14} />,
      onClick: () => setConfirmando("reabrir"),
    });
  }
  if (!["RESOLVIDA", "CANCELADA"].includes(a.situacao) && podeEscalonar) {
    actions.push({
      label: "Escalonar", icon: <FiArrowUpCircle size={14} />,
      onClick: () => setConfirmando("escalonar"),
    });
  }
  actions.push({
    label: "Comentar", icon: <FiMessageCircle size={14} />,
    onClick: () => setConfirmando("comentar"),
  });

  const kpis = [
    {
      label: "Pedido",
      value: a.pedido_numero,
      sub: a.cliente_nome,
      tone: "neutral",
    },
    {
      label: "Tipo",
      value: TIPO_LABELS[a.tipo] || a.tipo,
      tone: "neutral",
    },
    {
      label: "Prioridade",
      value: PRIORIDADE_LABELS[a.prioridade] || a.prioridade,
      tone: PRIORIDADE_TONES[a.prioridade] || "neutral",
    },
    {
      label: "SLA",
      value: a.atrasada
        ? formatarHorasRestantes(a.horas_restantes)
        : formatarHorasRestantes(a.horas_restantes),
      tone: a.atrasada ? "danger" : (a.horas_restantes < 4 ? "warning" : "success"),
      sub: `${a.sla_horas}h totais`,
    },
  ];

  return (
    <PageContainer>
      <EntityHeader
        title={a.numero}
        subtitle={a.titulo}
        badges={[
          {
            label: SITUACAO_LABELS[a.situacao] || a.situacao,
            tone: SITUACAO_TONES[a.situacao] || "neutral",
          },
          ...(a.atrasada ? [{ label: "Atrasada", tone: "danger" }] : []),
          ...(a.escalonada ? [{ label: "Escalada", tone: "warning" }] : []),
        ]}
        actions={actions}
      />

      <KpiStrip items={kpis} />

      {error && <ErrorMsg>{error}</ErrorMsg>}

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
        gap: 16,
      }}>
        <Card>
          <SectionTitle>Dados</SectionTitle>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 14 }}>
            <div><strong>Pedido:</strong> {a.pedido_numero}</div>
            <div><strong>Cliente:</strong> {a.cliente_nome}</div>
            <div><strong>Tipo:</strong> {TIPO_LABELS[a.tipo] || a.tipo}</div>
            <div><strong>Prioridade:</strong> {PRIORIDADE_LABELS[a.prioridade] || a.prioridade}</div>
            <div><strong>SLA:</strong> {a.sla_horas} horas</div>
            <div><strong>Prazo:</strong> {a.prazo_sla ? fmtData(a.prazo_sla) : "—"}</div>
            <hr style={{ border: "none", borderTop: "1px solid var(--border, #e5e7eb)", margin: "4px 0" }} />
            <div><strong>Aberta em:</strong> {fmtData(a.aberta_em)}</div>
            {a.em_analise_em && <div><strong>Em análise em:</strong> {fmtData(a.em_analise_em)}</div>}
            {a.em_resolucao_em && <div><strong>Em resolução em:</strong> {fmtData(a.em_resolucao_em)}</div>}
            {a.resolvida_em && <div><strong>Resolvida em:</strong> {fmtData(a.resolvida_em)}</div>}
            {a.cancelada_em && <div><strong>Cancelada em:</strong> {fmtData(a.cancelada_em)}</div>}
          </div>
        </Card>

        <Card>
          <SectionTitle>Responsável / Escalonamento</SectionTitle>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 14 }}>
            <div><strong>Responsável atual:</strong> {a.responsavel_nome || "—"}</div>
            {a.escalonada && (
              <>
                <hr style={{ border: "none", borderTop: "1px solid var(--border, #e5e7eb)", margin: "4px 0" }} />
                <div><strong>Escalonada para:</strong> {a.escalonada_para_usuario_nome || "—"}</div>
                <div><strong>Em:</strong> {a.escalonada_em ? fmtData(a.escalonada_em) : "—"}</div>
                <div><strong>Motivo:</strong> {a.motivo_escalonamento}</div>
              </>
            )}
          </div>
        </Card>
      </div>

      <Card>
        <SectionTitle>Descrição do cliente</SectionTitle>
        <div style={{ whiteSpace: "pre-wrap", fontSize: 14 }}>{a.descricao}</div>
      </Card>

      {a.resolucao && (
        <Card>
          <SectionTitle><Pill $tone="success">Resolução aplicada</Pill></SectionTitle>
          <div style={{ whiteSpace: "pre-wrap", fontSize: 14 }}>{a.resolucao}</div>
        </Card>
      )}

      {a.motivo_cancelamento && (
        <Card>
          <SectionTitle><Pill $tone="neutral">Motivo do cancelamento</Pill></SectionTitle>
          <div style={{ whiteSpace: "pre-wrap", fontSize: 14 }}>{a.motivo_cancelamento}</div>
        </Card>
      )}

      <Card>
        <SectionTitle>Histórico ({a.eventos?.length || 0} eventos)</SectionTitle>
        {(!a.eventos || a.eventos.length === 0) ? (
          <EmptyMsg>Nenhum evento.</EmptyMsg>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {a.eventos.map((ev) => (
              <div
                key={ev.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "160px 1fr",
                  gap: 12,
                  paddingBottom: 12,
                  borderBottom: "1px solid var(--border, #e5e7eb)",
                }}
              >
                <div style={{ fontSize: 12, opacity: 0.7 }}>{fmtData(ev.timestamp)}</div>
                <div>
                  <Pill $tone={EVENTO_TONES[ev.tipo] || "neutral"}>{ev.tipo_display}</Pill>
                  {ev.descricao && (
                    <div style={{ marginTop: 4, fontSize: 14, whiteSpace: "pre-wrap" }}>
                      {ev.descricao}
                    </div>
                  )}
                  <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>
                    por {ev.ator_nome}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {confirmando && (
        <SlideOverPanel
          open
          onClose={() => { setConfirmando(null); setTexto(""); setNovoResponsavelId(""); }}
          title={({
            iniciar_analise: "Iniciar análise",
            iniciar_resolucao: "Iniciar resolução",
            resolver: "Resolver assistência",
            cancelar: "Cancelar assistência",
            reabrir: "Reabrir assistência",
            escalonar: "Escalonar assistência",
            comentar: "Adicionar comentário",
          })[confirmando]}
          footer={
            <AsyncButton
              variant={
                ["cancelar", "reabrir"].includes(confirmando) ? "danger" : "primary"
              }
              onClick={executar}
              successMessage={`Ação executada`}
            >
              Confirmar
            </AsyncButton>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {confirmando === "escalonar" && (
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>Novo responsável *</span>
                <Select value={novoResponsavelId}
                        onChange={(e) => setNovoResponsavelId(e.target.value)}>
                  <option value="">Selecione</option>
                  {usuarios.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nome || u.email}
                    </option>
                  ))}
                </Select>
              </label>
            )}
            {confirmando !== "iniciar_analise" && confirmando !== "iniciar_resolucao" && (
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>
                  {confirmando === "resolver" && "Resolução aplicada *"}
                  {confirmando === "cancelar" && "Motivo do cancelamento *"}
                  {confirmando === "reabrir" && "Motivo da reabertura *"}
                  {confirmando === "escalonar" && "Motivo do escalonamento *"}
                  {confirmando === "comentar" && "Comentário *"}
                </span>
                <Textarea
                  rows={4} value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  placeholder={
                    confirmando === "resolver"
                      ? "Descreva a resolução (substituiu, ajustou, esclareceu...)"
                      : "Texto obrigatório"
                  }
                  maxLength={500}
                />
              </label>
            )}
            <div style={{ fontSize: 13, opacity: 0.8 }}>
              {confirmando === "iniciar_analise" && "Time pós-venda investigando. Marca como EM_ANALISE."}
              {confirmando === "iniciar_resolucao" && "Plano de ação em execução. Marca como EM_RESOLUCAO."}
              {confirmando === "resolver" && "Cliente satisfeito. Marca como RESOLVIDA. Terminal."}
              {confirmando === "cancelar" && "Aborta a assistencia (cliente desistiu / nao se aplica). Terminal."}
              {confirmando === "reabrir" && "Cliente voltou a reclamar. Volta para EM_ANALISE."}
              {confirmando === "escalonar" && "Reassinala para um responsável mais alto + audita motivo."}
              {confirmando === "comentar" && "Adiciona comentário interno na timeline (não muda situação)."}
            </div>
          </div>
        </SlideOverPanel>
      )}
    </PageContainer>
  );
}
